import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ServiceProductCard from '@/components/document-services/ServiceProductCard';
import {
  ServiceDisabledNotice,
  ServiceEmpty,
  ServiceError,
  ServiceLoading,
  ServicePageHeader,
} from '@/components/document-services/DocumentServicesCommon';
import { getMyServiceProducts } from '@/lib/api';
import { apiErrorMessage, apiItems, orderingEnabled } from '@/lib/documentServices';

export default function BrowseServices() {
  const [products, setProducts] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getMyServiceProducts({ status: 'published' });
      setProducts(apiItems(data, ['products']));
      setEnabled(orderingEnabled(data, true));
    } catch (err) {
      setError(apiErrorMessage(err, 'Available services could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 animate-fade-in">
      <ServicePageHeader
        eyebrow="Services & Orders"
        title="Browse document services"
        description="Order official MCC documents, select delivery and processing options, and review the exact tax-exempt total before payment."
        actions={<Button asChild variant="outline" className="border-[#062B1A]/15 text-[#062B1A]"><Link to="/student/orders"><ClipboardList className="mr-2 size-4" />My Orders</Link></Button>}
      />
      {!enabled && !loading && <ServiceDisabledNotice />}
      <div className="flex items-start gap-2 rounded-lg border border-[#062B1A]/10 bg-white px-4 py-3 text-xs leading-relaxed text-[#062B1A]/55">
        <ShieldCheck className="mt-0.5 size-4 shrink-0 text-[#D4AF37]" />
        Prices are shown in Canadian dollars. Document services are tax exempt and MCC adds no checkout surcharge.
      </div>
      {loading ? <ServiceLoading /> : error ? <ServiceError message={error} onRetry={load} /> : products.length === 0 ? (
        <ServiceEmpty title="No services are published yet" description="MCC is preparing the Document Services catalogue. Please check back later." />
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {products.map((product) => <ServiceProductCard key={product.id || product.uuid || product.slug} product={product} />)}
        </div>
      )}
    </div>
  );
}
